// ═══════════════════════════════════════════════════════════════
// ProfileSheet.web.tsx: web variant of the thread `…` sheet.
//
// RN `<Modal>` doesn't render cleanly on web: the repo's pattern is
// `<WebDialog>` (a portal-based shell from common/WebDialog.web.tsx).
// This file overrides the native sheet for web users via Metro's
// platform-extensions resolution.
//
// Shared business logic (block/mute/report mutations) is identical to
// ProfileSheet.tsx: keep the two in sync. Differences are layout-only.
// ═══════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Slash, Flag } from 'lucide-react';
import { WebDialog } from '@/components/common/WebDialog.web';
import { useTheme } from '@/hooks/useTheme';
import { useLocale } from '@/hooks/useLocale';
import { useToast } from '@/hooks/useToast';
import {
  useBlockUser,
  useMuteConversation,
  useReportUser,
  useUnmuteConversation,
} from '@/hooks/useMessagingPreferences';
import { createUserReportRequestId } from '@/lib/user-report';

export interface ProfileSheetProps {
  visible: boolean;
  conversationId: string;
  notificationsMuted: boolean;
  recipient: { id: string; name: string };
  onClose: () => void;
  onEditProfile: () => void;
  onBlocked?: () => void;
  onMuteChanged?: (muted: boolean) => void;
}

export function ProfileSheet({
  visible,
  conversationId,
  notificationsMuted,
  recipient,
  onClose,
  onEditProfile,
  onBlocked,
  onMuteChanged,
}: ProfileSheetProps) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const { t } = useTranslation('common');
  const toast = useToast();
  const block = useBlockUser();
  const mute = useMuteConversation();
  const unmute = useUnmuteConversation();
  const report = useReportUser();
  const reportRequestIdRef = useRef<string | null>(null);
  const reportInFlightRef = useRef(false);

  useEffect(() => {
    if (!visible) return;
    reportRequestIdRef.current = createUserReportRequestId();
    reportInFlightRef.current = false;
  }, [conversationId, recipient.id, visible]);

  const handleBlock = useCallback(async () => {
    try {
      await block.mutateAsync({ blockedUserId: recipient.id });
      toast.success(
        t('msgSheet.blockedTitle'),
        t('msgSheet.blockedBody', { name: recipient.name }),
      );
      onBlocked?.();
      onClose();
    } catch (e) {
      toast.error(
        t('msgSheet.blockFailedTitle'),
        e instanceof Error ? e.message : '',
      );
    }
  }, [block, recipient, onBlocked, onClose, t, toast]);

  const handleMute = useCallback(async () => {
    try {
      if (notificationsMuted) {
        await unmute.mutateAsync(conversationId);
        onMuteChanged?.(false);
        toast.success(t('msgSheet.unmutedTitle'), t('msgSheet.unmutedBody'));
      } else {
        await mute.mutateAsync({ conversationId, duration: 'indefinite' });
        onMuteChanged?.(true);
        toast.success(t('msgSheet.mutedTitle'), t('msgSheet.mutedBody'));
      }
      onClose();
    } catch (e) {
      toast.error(
        t(
          notificationsMuted
            ? 'msgSheet.unmuteFailedTitle'
            : 'msgSheet.muteFailedTitle',
        ),
        e instanceof Error ? e.message : '',
      );
    }
  }, [
    conversationId,
    mute,
    notificationsMuted,
    onClose,
    onMuteChanged,
    t,
    toast,
    unmute,
  ]);

  const handleReport = useCallback(async () => {
    if (reportInFlightRef.current) return;
    reportInFlightRef.current = true;
    const clientRequestId =
      reportRequestIdRef.current ?? createUserReportRequestId();
    reportRequestIdRef.current = clientRequestId;
    try {
      await report.mutateAsync({
        reportedUserId: recipient.id,
        conversationId,
        category: 'other',
        clientRequestId,
      });
      toast.success(t('msgSheet.reportedTitle'), t('msgSheet.reportedBody'));
      onClose();
    } catch (e) {
      toast.error(
        t('msgSheet.reportFailedTitle'),
        e instanceof Error ? e.message : '',
      );
    } finally {
      reportInFlightRef.current = false;
    }
  }, [report, recipient, conversationId, onClose, t, toast]);

  if (!visible) return null;

  return (
    <WebDialog
      title={recipient.name}
      onClose={onClose}
      maxWidth={420}
      footer={
        <button onClick={onClose} style={cancelBtn(theme)}>
          {t('actions.cancel')}
        </button>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '4px 0 12px',
        }}
      >
        {/*<Row*/}
        {/*  icon={<Edit3 size={18} strokeWidth={1.8} color={theme.textSecondary} />}*/}
        {/*  label={t('msgSheet.editProfile')}*/}
        {/*  onClick={() => {*/}
        {/*    onClose();*/}
        {/*    onEditProfile();*/}
        {/*  }}*/}
        {/*  theme={theme}*/}
        {/*  isRTL={isRTL}*/}
        {/*/>*/}
        <Row
          icon={
            notificationsMuted ? (
              <Bell size={18} strokeWidth={1.8} color={theme.textSecondary} />
            ) : (
              <BellOff
                size={18}
                strokeWidth={1.8}
                color={theme.textSecondary}
              />
            )
          }
          label={t(
            notificationsMuted
              ? 'msgSheet.unmuteNotifications'
              : 'msgSheet.muteNotifications',
          )}
          onClick={handleMute}
          theme={theme}
          isRTL={isRTL}
        />
        <Row
          icon={<Slash size={18} strokeWidth={1.8} color="#f87171" />}
          label={t('msgSheet.blockPerson', { name: recipient.name })}
          onClick={handleBlock}
          danger
          theme={theme}
          isRTL={isRTL}
        />
        <Row
          icon={
            <Flag size={18} strokeWidth={1.8} color={theme.textSecondary} />
          }
          label={t('msgSheet.report')}
          onClick={handleReport}
          disabled={report.isPending}
          theme={theme}
          isRTL={isRTL}
        />
      </div>
    </WebDialog>
  );
}

function Row({
  icon,
  label,
  onClick,
  danger,
  theme,
  isRTL,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  theme: any;
  isRTL: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor =
          theme.surfaceSecondary;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
      }}
      style={{
        display: 'flex',
        direction: isRTL ? 'rtl' : 'ltr',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        padding: '12px 14px',
        borderRadius: 10,
        border: 0,
        background: 'transparent',
        color: danger ? '#f87171' : theme.text,
        fontSize: 14.5,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        width: '100%',
        textAlign: 'start',
      }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

function cancelBtn(theme: any): React.CSSProperties {
  return {
    width: '100%',
    padding: '11px 0',
    borderRadius: 10,
    border: `1px solid ${theme.borderSubtle}`,
    background: theme.surfaceSecondary,
    color: theme.text,
    fontSize: 14.5,
    fontWeight: 600,
    cursor: 'pointer',
  };
}
